import {
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';

import {ISelectionSlice} from './types';

const initialState: ISelectionSlice = {};

/**
 * Builds a row-selection slice for one feature module (FNP-951).
 *
 * Every module gets its OWN slice, keyed by `entityKey`, so selecting a
 * customer cannot show up as a selected invoice. State is
 * `Record<recordId, record>`; see ISelectionSlice for why the record and not a
 * bare boolean.
 *
 * Mutating syntax below is Immer, which RTK applies inside createSlice — these
 * look like in-place writes but produce a new state object. `delete` on a draft
 * is likewise fine and is how a key is genuinely REMOVED rather than set to
 * undefined; a lingering `undefined` key would still be counted by
 * `Object.keys`, which is exactly how a "cleared" selection keeps reporting a
 * non-zero count.
 */
export const SelectionSliceFor = (entityKey: string) => {
    return createSlice({
        name: entityKey,
        initialState,
        reducers: {
            addToSelection: (
                state,
                action: PayloadAction<{id: string} & Record<string, unknown>>,
            ) => {
                const record = action.payload;
                state[record.id] = record;
            },
            /**
             * Select-all in one dispatch. console-app's copy of this factory
             * takes a single record here — identical to addToSelection despite
             * the name — so a select-all there costs one dispatch and one
             * re-render PER ROW. This takes the array.
             */
            addManyToSelection: (
                state,
                action: PayloadAction<({id: string} & Record<string, unknown>)[]>,
            ) => {
                action.payload.forEach((record) => {
                    state[record.id] = record;
                });
            },
            removeFromSelection: (state, action: PayloadAction<string>) => {
                delete state[action.payload];
            },
            clearSelection: (state) => {
                Object.keys(state)
                      .forEach((key) => {
                          delete state[key];
                      });
            },
        },
    });
};
